import { releaseNativeDocumentResources } from '../public-format/native-resources';
import { createId, type EditorDocument } from './model';
import { HistoryManager, type HistoryEvent } from './commands';
import { RevisionLedger } from '../semantic/ledger';

export interface SessionRevision {
  sessionId: string;
  revision: number;
  document: EditorDocument;
}

export interface SessionRecoveryState {
  revision: number;
  status: 'idle' | 'saving' | 'saved' | 'failed';
  savedAt?: string;
  message?: string;
  durationMs?: number;
}

export interface SessionFileHandle {
  name: string;
  createWritable(): Promise<{
    write(data: Blob | BufferSource): Promise<void>;
    close(): Promise<void>;
    abort?(): Promise<void>;
  }>;
}

/** Runtime identity and revision state. Persistent document IDs are intentionally not unique tabs. */
export class DocumentSession {
  readonly sessionId: string;
  readonly history: HistoryManager;
  /**
   * Revision ledger (ADR-009 §6.3), attached before any edit can happen so it
   * covers every revision since the session was created, whatever its origin.
   */
  readonly ledger: RevisionLedger;
  currentRevision = 0;
  savedRevision = 0;
  fileHandle?: SessionFileHandle;
  recovery: SessionRecoveryState = { revision: 0, status: 'idle' };
  private disposed = false;
  private readonly lifetime = new AbortController();
  get closedSignal(): AbortSignal { return this.lifetime.signal; }
  private listeners = new Set<(event: HistoryEvent) => void>();

  constructor(readonly document: EditorDocument, options: { byteBudget?: number; initiallySaved?: boolean; ledgerCapacity?: number; sessionId?: string } = {}) {
    // Headless tooling passes its own ID so it consumes no randomness (its
    // parity check pins crypto.randomUUID and compares layer IDs byte for byte).
    this.sessionId = options.sessionId ?? createId('session');
    this.history = new HistoryManager(options.byteBudget);
    // Subscribed first, so the ledger has recorded a revision before any
    // session listener (the UI) reacts to it.
    this.ledger = new RevisionLedger(this, options.ledgerCapacity);
    this.history.subscribe((event) => {
      this.currentRevision = event.revision;
      for (const listener of this.listeners) listener(event);
    });
    if (options.initiallySaved === false) this.savedRevision = -1;
  }

  get dirty(): boolean {
    return this.currentRevision !== this.savedRevision || this.history.hasLiveCommand;
  }

  subscribe(listener: (event: HistoryEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  captureRevision(): SessionRevision {
    if (this.disposed) throw new Error('Document session is closed.');
    if (this.history.hasLiveCommand) throw new Error('Finish the active edit before saving this document.');
    return { sessionId: this.sessionId, revision: this.currentRevision, document: this.document };
  }

  markSaved(revision: number): void {
    if (!Number.isSafeInteger(revision) || revision < 0 || revision > this.currentRevision) {
      throw new Error('Cannot mark an invalid or future document revision as saved.');
    }
    this.savedRevision = revision;
  }

  isCurrent(binding: Pick<SessionRevision, 'sessionId' | 'revision'>): boolean {
    return !this.disposed && binding.sessionId === this.sessionId && binding.revision === this.currentRevision;
  }

  dispose(): void {
    if (this.disposed) return;
    this.lifetime.abort();
    this.history.cancelLive(this.document);
    this.history.clear();
    this.listeners.clear();
    this.ledger.dispose();
    releaseNativeDocumentResources(this.document);
    this.disposed = true;
  }
}
